/**
 * Search-typeahead suggestion sources.
 *
 * The list pages keep a free-text `q` filter as the source of truth (the
 * table still filters live, and matches descriptions). These helpers power
 * the *optional* autocomplete dropdown that floats under the search box:
 * each fetcher hits the same RO list endpoint the page already uses, caps
 * the result to a short slice, and maps it to a navigable suggestion.
 *
 * Selecting a suggestion navigates to that entity's detail route; the page
 * owns the navigation (these helpers only supply the `href`). Platform-
 * neutral: no DOM, no react-router — just the adapter and the route manifest.
 */

import type { DataAdapter } from "../data/adapter";
import { paths } from "../routing/routes";

export interface SearchSuggestion {
  /** Unique within a result set (entity id is sufficient per group). */
  id: string;
  /** Section heading, e.g. "Customers" | "Jobs" | "Estimates" | "Orders". */
  group: string;
  /** Primary line. */
  label: string;
  /** Optional secondary line (customer name, job number, …). */
  hint?: string;
  /** Canonical detail route to navigate to on select. */
  href: string;
}

/** Per-source cap. Keeps the dropdown short and the queries cheap. */
const SUGGESTION_LIMIT = 5;

function isBlank(query: string): boolean {
  return query.trim().length === 0;
}

export async function fetchCustomerSuggestions(
  adapter: DataAdapter,
  query: string,
): Promise<SearchSuggestion[]> {
  if (isBlank(query)) return [];
  const resp = await adapter.getCustomers({
    q: query.trim(),
    page: 1,
    pageSize: SUGGESTION_LIMIT,
  });
  return resp.items.map((c) => ({
    id: c.id,
    group: "Customers",
    label: c.name,
    href: paths.customerDetail({ customerId: c.id }),
  }));
}

export async function fetchJobSuggestions(
  adapter: DataAdapter,
  query: string,
): Promise<SearchSuggestion[]> {
  if (isBlank(query)) return [];
  const resp = await adapter.getWorkPortfolio({
    q: query.trim(),
    page: 1,
    pageSize: SUGGESTION_LIMIT,
  });
  return resp.items.map((j) => ({
    id: j.id,
    group: "Jobs",
    label: j.title || `Job ${j.job_no}`,
    hint: `${j.job_no} · ${j.customer_name}`,
    href: paths.jobWorkspace({ jobNo: j.job_no }),
  }));
}

export async function fetchEstimateSuggestions(
  adapter: DataAdapter,
  query: string,
): Promise<SearchSuggestion[]> {
  if (isBlank(query)) return [];
  const resp = await adapter.getEstimates({
    q: query.trim(),
    page: 1,
    pageSize: SUGGESTION_LIMIT,
  });
  return resp.items.map((e) => ({
    id: e.id,
    group: "Estimates",
    label: e.description || "Estimate",
    hint: e.customer?.name ?? undefined,
    href: paths.estimateDetail({ id: e.id }),
  }));
}

export async function fetchChargeableJobSuggestions(
  adapter: DataAdapter,
  query: string,
): Promise<SearchSuggestion[]> {
  if (isBlank(query)) return [];
  const resp = await adapter.getChargeableJobs({
    q: query.trim(),
    page: 1,
    pageSize: SUGGESTION_LIMIT,
  });
  // job_no is nullable on JobViewItem (a freshly-synced row can lack it
  // briefly) — a suggestion with nowhere to navigate is worse than one
  // fewer suggestion, so those rows are excluded rather than linked by
  // internal id.
  return resp.items
    .filter((j): j is typeof j & { job_no: string } => j.job_no != null)
    .map((j) => ({
      id: j.id,
      group: "Jobs",
      label: j.description || "Job",
      hint: j.customer?.name ?? undefined,
      href: paths.jobWorkspace({ jobNo: j.job_no }),
    }));
}

/**
 * Run several suggestion sources concurrently and concatenate, preserving
 * the order the sources were passed (so callers control group ordering). A
 * source that throws contributes nothing rather than failing the dropdown.
 */
export async function gatherSuggestions(
  sources: Array<
    (adapter: DataAdapter, query: string) => Promise<SearchSuggestion[]>
  >,
  adapter: DataAdapter,
  query: string,
): Promise<SearchSuggestion[]> {
  const results = await Promise.all(
    sources.map((source) => source(adapter, query).catch(() => [])),
  );
  return results.flat();
}
